/**
 * Construction capital band — the loan-level answer to "where does this loan
 * stand against its holdback?" (redesign rationale §1).
 *
 * Everything here comes from `rollup` on GET .../draws/{dealId}
 * (baseLoanAmount, totalHoldback, totalDrawnToDate, remainingHoldback). The one
 * client-side number is `budgetTotal` — Σ estimatedTotalLineItemCost across the
 * sheets, which the backend does not serialize as a field. It exists only to
 * surface the budget-vs-holdback overage; nothing is persisted from it.
 *
 * The stacked bar renders base / drawn / remaining as fractions of the total
 * loan (base + holdback), so at full drawdown it reads base + holdback = total —
 * the same tie-out the principal-balance progression closes.
 */
import { useMemo } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddRounded from "@mui/icons-material/AddRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { T, cardSx, labelSx, money, num, pct } from "./budgetDrawsTheme";

// Hatched fill: unreleased holdback is provisional, and a texture reads as "not
// yet solid" without needing the legend to say it.
const HATCH = `repeating-linear-gradient(-45deg, ${T.accentTint}, ${T.accentTint} 6px, ${T.accentTint2} 6px, ${T.accentTint2} 12px)`;

const LegendSwatch = ({ variant }) => (
  <Box
    sx={{
      width: 11,
      height: 11,
      borderRadius: "3px",
      flex: "none",
      ...(variant === "base" && { bgcolor: T.ink }),
      ...(variant === "drawn" && { bgcolor: T.accent }),
      ...(variant === "remaining" && {
        background: `repeating-linear-gradient(-45deg, ${T.accentTint}, ${T.accentTint} 3px, ${T.accentTint2} 3px, ${T.accentTint2} 6px)`,
        border: `1px solid ${T.accentTint2}`,
      }),
    }}
  />
);

const LegendItem = ({ variant, label, value }) => (
  <Flx ac sx={{ gap: 1 }}>
    <LegendSwatch variant={variant} />
    <Txt sx={{ fontSize: 12, color: T.muted }}>{label}</Txt>
    <Txt sx={{ fontSize: 12, fontWeight: 600, color: T.ink, fontFamily: T.mono }}>
      {value}
    </Txt>
  </Flx>
);

const Stat = ({ label, value, sub, accent }) => (
  <Box sx={{ bgcolor: T.card, p: "12px 16px" }}>
    <Txt sx={{ ...labelSx, display: "block", mb: 0.4 }}>{label}</Txt>
    <Txt
      sx={{
        display: "block",
        fontFamily: T.mono,
        fontSize: 19,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: accent ? T.accentInk : T.ink,
      }}
    >
      {value}
    </Txt>
    {sub && (
      <Txt sx={{ display: "block", fontSize: 12, color: T.muted, mt: 0.25 }}>
        {sub}
      </Txt>
    )}
  </Box>
);

const CapitalOverviewCard = ({
  rollup,
  drawCount = 0,
  budgetTotal = 0, // Σ of LOCKED (final) sheets only
  budgetFinal, // { finalCount, sheetCount, lockedTotal }
  eligible,
  onNewDraw,
}) => {
  const { base, holdback, drawn, remaining, total, widths } = useMemo(() => {
    const b = num(rollup?.baseLoanAmount);
    const h = num(rollup?.totalHoldback);
    const d = num(rollup?.totalDrawnToDate);
    // Prefer the server's remaining; fall back to the subtraction if absent.
    const r = rollup?.remainingHoldback == null ? Math.max(0, h - d) : num(rollup.remainingHoldback);
    const t = b + h;
    const w = (v) => (t > 0 ? `${Math.max(0, (v / t) * 100)}%` : "0%");
    return {
      base: b,
      holdback: h,
      drawn: d,
      remaining: r,
      total: t,
      widths: { base: w(b), drawn: w(d), remaining: w(r) },
    };
  }, [rollup]);

  // Budget overage: the delta is work the draw funds will not cover. Conditional
  // on purpose — a warning that always shows is a warning nobody reads.
  const overage = budgetTotal > 0 && holdback > 0 ? budgetTotal - holdback : 0;

  return (
    <Box sx={{ ...cardSx, p: "20px 22px 18px" }}>
      <Flx jb ac wrap sx={{ gap: 1.5, mb: 1.75, alignItems: "baseline" }}>
        <Txt sx={{ ...labelSx, fontSize: 12, letterSpacing: "0.08em", color: T.muted }}>
          Construction capital
        </Txt>
        {budgetFinal?.sheetCount ? (
          <Txt
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color:
                budgetFinal.finalCount === budgetFinal.sheetCount ? T.green : T.amberInk,
            }}
          >
            {budgetFinal.finalCount} of {budgetFinal.sheetCount}{" "}
            {budgetFinal.sheetCount === 1 ? "budget" : "budgets"} final
            {budgetFinal.finalCount ? (
              <Box component="span" sx={{ fontFamily: T.mono, color: T.ink2, fontWeight: 600 }}>
                {" "}· {money(budgetFinal.lockedTotal)}
              </Box>
            ) : null}
          </Txt>
        ) : null}
        <Txt sx={{ fontFamily: T.mono, fontSize: 12, color: T.muted }}>
          Total loan{" "}
          <Box component="span" sx={{ color: T.ink, fontWeight: 600 }}>
            {money(total)}
          </Box>
        </Txt>
      </Flx>

      <Box
        role="img"
        aria-label={`Base loan ${money(base)}, drawn to date ${money(drawn)}, remaining holdback ${money(remaining)}`}
        sx={{
          position: "relative",
          height: 34,
          borderRadius: "8px",
          overflow: "hidden",
          display: "flex",
          bgcolor: T.line2,
        }}
      >
        <Box sx={{ height: "100%", width: widths.base, bgcolor: T.ink, transition: "width .5s cubic-bezier(.2,.8,.2,1)" }} />
        <Box sx={{ height: "100%", width: widths.drawn, bgcolor: T.accent, transition: "width .5s cubic-bezier(.2,.8,.2,1)" }} />
        <Box sx={{ height: "100%", width: widths.remaining, background: HATCH, transition: "width .5s cubic-bezier(.2,.8,.2,1)" }} />
      </Box>

      <Flx wrap sx={{ gap: 2.5, mt: 1.5 }}>
        <LegendItem variant="base" label="Base loan" value={money(base)} />
        <LegendItem variant="drawn" label="Drawn to date" value={money(drawn)} />
        <LegendItem variant="remaining" label="Remaining holdback" value={money(remaining)} />
      </Flx>

      {/* 1px gaps over a line-colored background give hairline dividers without
          per-cell borders that double up at the seams. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(4, minmax(0,1fr))" },
          gap: "1px",
          bgcolor: T.line2,
          borderRadius: "8px",
          overflow: "hidden",
          mt: 2,
        }}
      >
        <Stat label="Base loan" value={money(base)} sub="Funded at close" />
        <Stat label="Total holdback" value={money(holdback)} sub="Released in draws" />
        <Stat
          label="Drawn to date"
          value={money(drawn)}
          sub={`${drawCount} ${drawCount === 1 ? "draw" : "draws"}`}
        />
        <Stat
          accent
          label="Remaining holdback"
          value={money(remaining)}
          sub={holdback > 0 ? `${pct(remaining / holdback, 0)} of holdback left` : undefined}
        />
      </Box>

      {/* The releasable-funds answer, restated here with the action next to it —
          so "how much can we draw, and start one" is settled in the band the
          operator lands on, without scrolling past the budget to the ledger. */}
      {eligible && (
        <Flx
          jb
          ac
          wrap
          sx={{
            gap: 1.75,
            mt: 1.75,
            p: "11px 14px",
            borderRadius: T.radiusSm,
            bgcolor: T.accentTint,
            border: `1px solid ${T.accentTint2}`,
          }}
        >
          <Txt sx={{ fontSize: 12, color: T.accentInk }}>
            {eligible.lead}{" "}
            <Box component="span" sx={{ fontFamily: T.mono, fontWeight: 600 }}>
              {pct(eligible.basis, eligible.digits)}
            </Box>{" "}
            complete, up to{" "}
            <Box component="span" sx={{ fontFamily: T.mono, fontWeight: 600 }}>
              {money(eligible.amount)}
            </Box>{" "}
            {eligible.tail}
          </Txt>
          {onNewDraw && (
            <Button
              variant="contained"
              size="small"
              disableElevation
              startIcon={<AddRounded sx={{ fontSize: 17 }} />}
              onClick={onNewDraw}
              sx={{
                textTransform: "none",
                bgcolor: T.accent,
                "&:hover": { bgcolor: T.accentInk },
              }}
            >
              New draw
            </Button>
          )}
        </Flx>
      )}

      {overage > 0 && (
        <Flx
          sx={{
            gap: 1,
            alignItems: "flex-start",
            mt: 1.75,
            p: "9px 12px",
            borderRadius: T.radiusSm,
            bgcolor: T.amberTint,
            border: `1px solid ${T.amberLine}`,
          }}
        >
          <WarningAmberRounded sx={{ fontSize: 15, color: T.amberInk, mt: "1px", flex: "none" }} />
          <Txt sx={{ fontSize: 12, color: T.amberInk }}>
            Final budget total {money(budgetTotal)} exceeds the holdback by {money(overage)}. The
            overage is not covered by draw funds.
          </Txt>
        </Flx>
      )}
    </Box>
  );
};

export default CapitalOverviewCard;
